<template>
  <div class="flex flex-col w-full h-full overflow-y-auto px-4 pb-8 gap-4">
    <!-- Dates -->
    <div class="flex items-center justify-between gap-3 pt-2 text-center">
      <div class="flex flex-col flex-1">
        <span class="text-[11px] text-gray-400">סריקה קודמת</span>
        <span class="text-sm font-medium">{{ formatScanDate(comparison.previousDate) }}</span>
      </div>
      <i class="pi pi-arrow-left text-gray-300" />
      <div class="flex flex-col flex-1">
        <span class="text-[11px] text-gray-400">סריקה עדכנית</span>
        <span class="text-sm font-medium">{{ formatScanDate(comparison.currentDate) }}</span>
      </div>
    </div>

    <!-- Overall delta -->
    <div class="flex items-center justify-center gap-2 p-4 rounded-xl bg-gray-50">
      <span class="text-sm text-gray-600">שינוי בציון הכללי:</span>
      <span class="text-lg font-bold tabular-nums" :style="{ color: deltaColor(comparison.overallDelta) }">
        {{ comparison.overallDelta > 0 ? '+' : '' }}{{ comparison.overallDelta }}
      </span>
      <i
        v-if="comparison.overallDelta !== 0"
        :class="comparison.overallDelta > 0 ? 'pi pi-arrow-up' : 'pi pi-arrow-down'"
        :style="{ color: deltaColor(comparison.overallDelta) }"
        style="font-size: 0.8rem"
      />
    </div>

    <!-- AI narrative -->
    <p v-if="comparison.summary" class="text-sm text-gray-700 leading-relaxed bg-primary-50/40 border border-primary-100 rounded-lg p-3">
      {{ comparison.summary }}
    </p>

    <!-- Per-concern deltas -->
    <div class="flex flex-col gap-1">
      <h3 class="text-sm font-semibold text-gray-800 mb-1">שינוי לפי מדד</h3>
      <div
        v-for="d in sortedDeltas"
        :key="d.concern"
        class="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0"
      >
        <span class="text-sm text-gray-700 flex-1">{{ d.label }}</span>
        <span class="text-xs text-gray-400 tabular-nums">{{ d.previousScore }} → {{ d.currentScore }}</span>
        <span
          class="text-sm font-semibold tabular-nums w-12 text-left flex items-center justify-end gap-1"
          :style="{ color: deltaColor(d.delta) }"
        >
          <i
            v-if="d.delta !== 0"
            :class="d.delta > 0 ? 'pi pi-arrow-up' : 'pi pi-arrow-down'"
            style="font-size: 0.6rem"
          />
          {{ d.delta > 0 ? '+' : '' }}{{ d.delta }}
        </span>
      </div>
    </div>

    <p class="text-[11px] text-gray-400 leading-relaxed border-t border-gray-100 pt-3">
      * ההשוואה היא אסתטית בלבד ואינה מהווה אבחנה רפואית.
    </p>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from 'vue';
import type { ISkinScanComparison } from '@torim-app/types';
import { deltaColor, formatScanDate } from './skin-scan.utils';

const props = defineProps({
  comparison: {
    type: Object as PropType<ISkinScanComparison>,
    required: true,
  },
});

// Biggest movers first (largest absolute change).
const sortedDeltas = computed(() =>
  [...props.comparison.concernDeltas].sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)),
);
</script>
